import { getApiUrl, type OrderPhotoResponseDto } from "Infrastructure/Api/Api";
import { UserPermissions } from "Infrastructure/Api/permissions";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { hasPermission } from "Infrastructure/Utilities/Permissions/hasPermission";
import { useUploadOrderPhotosMutation } from "Order/Api/Mutation/uploadOrderPhotosMutation";
import { useOrderPhotosQuery } from "Order/Api/Query/useOrderPhotosQuery";
import { BaseCard } from "Shared/Components/Cards/BaseCard";
import { VisuallyHiddenInput } from "Shared/Components/Form/CustomFormComponent/FormUploadButton";
import { CloudUpload, PictureAsPdf } from "@mui/icons-material";
import {
	Box,
	Chip,
	Dialog,
	Grid2,
	IconButton,
	Tooltip,
	Typography,
} from "@mui/material";
import { type ChangeEvent, useState } from "react";
import { toast } from "sonner";

type OrderPhotosWidgetProps = {
	orderId: number;
};

// Mirrors app/core order-photo.constants.ts; the server stays authoritative.
const MAX_PHOTOS_PER_ORDER = 5;
const MAX_PHOTO_MB = 10;
const MAX_PHOTO_BYTES = MAX_PHOTO_MB * 1024 * 1024;
const ACCEPTED_PHOTO_TYPES = [
	"image/jpeg",
	"image/png",
	"image/webp",
	"application/pdf",
];

const photosResource = Resources.base.order.photos;

// LocalStrategy returns a root-relative "/storage/…" path instead of a signed URL.
const resolvePhotoUrl = (url: string) =>
	url.startsWith("http") ? url : `${getApiUrl()}${url}`;

const photoAlt = (photo: OrderPhotoResponseDto) =>
	photo.originalFileName ?? `File ${photo.id}`;

export function OrderPhotosWidget({ orderId }: OrderPhotosWidgetProps) {
	const { t } = useResource();
	const { data: photos = [], isLoading } = useOrderPhotosQuery(orderId);
	const { mutate: uploadPhotos, isPending } =
		useUploadOrderPhotosMutation(orderId);
	const canUpload = hasPermission(UserPermissions.OrderModule.update);
	const [lightboxPhoto, setLightboxPhoto] =
		useState<OrderPhotoResponseDto | null>(null);

	const remaining = MAX_PHOTOS_PER_ORDER - photos.length;
	const uploadDisabled = isLoading || isPending || remaining <= 0;

	const handleFilesPicked = (event: ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(event.target.files ?? []);
		// Allow re-selecting the same file after a rejected or completed pick.
		event.target.value = "";
		// The label's `disabled` does not reach the nested native input.
		if (uploadDisabled) {
			return;
		}
		if (files.length === 0) {
			return;
		}
		if (files.length > remaining) {
			toast.warning(t(photosResource.tooMany, { remaining }));
			return;
		}
		for (const file of files) {
			if (!ACCEPTED_PHOTO_TYPES.includes(file.type)) {
				toast.warning(t(photosResource.unsupportedType, { name: file.name }));
				return;
			}
			if (file.size > MAX_PHOTO_BYTES) {
				toast.warning(
					t(photosResource.fileTooLarge, {
						name: file.name,
						maxMb: MAX_PHOTO_MB,
					}),
				);
				return;
			}
		}
		uploadPhotos(files);
	};

	const uploadAction = canUpload ? (
		<Tooltip
			title={
				remaining <= 0
					? t(photosResource.uploadDisabledFull, {
							max: MAX_PHOTOS_PER_ORDER,
						})
					: t(photosResource.upload)
			}
		>
			<span>
				<IconButton
					component="label"
					color="primary"
					disabled={uploadDisabled}
					aria-label={t(photosResource.upload)}
				>
					<CloudUpload />
					<VisuallyHiddenInput
						type="file"
						multiple
						disabled={uploadDisabled}
						accept={ACCEPTED_PHOTO_TYPES.join(",")}
						onChange={handleFilesPicked}
					/>
				</IconButton>
			</span>
		</Tooltip>
	) : undefined;

	return (
		<BaseCard
			title={t(photosResource.title)}
			action={uploadAction}
			isLoading={isLoading || isPending}
		>
			{isLoading ? (
				<></>
			) : photos.length === 0 ? (
				<Typography color="text.secondary">
					{t(photosResource.empty)}
				</Typography>
			) : (
				<Grid2 container spacing={1}>
					{photos.map((photo) => (
						<Grid2
							key={photo.id}
							size={{ xs: 6, sm: 4, md: 3 }}
							sx={{ position: "relative" }}
						>
							{photo.mimeType === "application/pdf" ? (
								<Box
									component="a"
									href={resolvePhotoUrl(photo.url)}
									target="_blank"
									rel="noopener noreferrer"
									sx={{
										height: 120,
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
										justifyContent: "center",
										gap: 0.5,
										p: 1,
										borderRadius: 1,
										bgcolor: "action.hover",
										color: "text.secondary",
										textDecoration: "none",
									}}
								>
									<PictureAsPdf fontSize="large" />
									<Typography
										variant="caption"
										noWrap
										sx={{ maxWidth: "100%" }}
									>
										{photoAlt(photo)}
									</Typography>
								</Box>
							) : (
								<Box
									component="img"
									src={resolvePhotoUrl(photo.url)}
									alt={photoAlt(photo)}
									loading="lazy"
									onClick={() => setLightboxPhoto(photo)}
									sx={{
										width: "100%",
										height: 120,
										objectFit: "cover",
										borderRadius: 1,
										cursor: "pointer",
									}}
								/>
							)}
							{photo.source === "admin" && (
								<Chip
									label={t(photosResource.adminBadge)}
									size="small"
									color="primary"
									sx={{ position: "absolute", top: 4, left: 4 }}
								/>
							)}
						</Grid2>
					))}
				</Grid2>
			)}
			<Dialog
				open={lightboxPhoto !== null}
				onClose={() => setLightboxPhoto(null)}
				maxWidth="lg"
				aria-label={t(photosResource.lightbox)}
			>
				{lightboxPhoto ? (
					<Box
						component="img"
						src={resolvePhotoUrl(lightboxPhoto.url)}
						alt={photoAlt(lightboxPhoto)}
						onClick={() => setLightboxPhoto(null)}
						sx={{ display: "block", maxWidth: "100%", maxHeight: "90vh" }}
					/>
				) : (
					<></>
				)}
			</Dialog>
		</BaseCard>
	);
}
